import { useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { RightClickContext } from '../components/common/JobListViewContext';
import AbstractListView from '../components/common/AbstractListView';

export const useContextMenu = (listView: AbstractListView) => {
  const [rightClickContext, setRightClickContext] = useState<RightClickContext | undefined>(undefined);
  const menuPositionRef = useRef({ top: 0, left: 0 });
  
  const isRightContextVisibleRef = useRef(false);
  const contextContainerRef = useRef<HTMLDivElement>(null);
  const listViewRef = useRef(listView);

  useEffect(() => {
    listViewRef.current = listView;
  }, [listView]);

  const handleClickOutsideContextMenu = useCallback((e: Event) => {
    if (isRightContextVisibleRef.current && contextContainerRef.current) {
      const target = e.target as HTMLElement;
      const shouldClose = !contextContainerRef.current.contains(target);

      if (shouldClose) {
        setRightClickContext(undefined);
        isRightContextVisibleRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideContextMenu, true);
    document.addEventListener('scroll', handleClickOutsideContextMenu, true);

    return () => {
      document.removeEventListener('click', handleClickOutsideContextMenu, true);
      document.removeEventListener('scroll', handleClickOutsideContextMenu, true);
    };
  }, [handleClickOutsideContextMenu]);

  const calculateMenuPosition = (node: Element, menuWidth = 150, menuHeight = 0) => {
    const rect = node.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let left = rect.right;
    let top = rect.bottom;

    if (left + menuWidth > windowWidth) {
      left = rect.left - menuWidth;
      if (left < 0) left = 0;
    }

    if (top + menuHeight > windowHeight) {
      top = rect.top - menuHeight;
      if (top < 0) top = 0;
    }

    return { top, left };
  };

  const onCellRightClick = useCallback((row: number, column: number, node: Element, e: MouseEvent) => {
    if (listViewRef.current.hasRightClickContext()) {
      e.preventDefault();
      isRightContextVisibleRef.current = true;

      const position = calculateMenuPosition(node);
      menuPositionRef.current = position;

      setRightClickContext((prev) => {
        if (prev?.row === row && prev?.column === column) {
          return prev;
        }
        return { row, column, node, status: false };
      });
    }
  }, []);

  const RightClickContextMenu = useCallback(() => {
    if (!rightClickContext) return null;
    const portalRoot = document?.getElementById('ifms-app-bottom');
    if (!portalRoot) return null;

    return (
      <Portal node={portalRoot}>
        <div
          onMouseMove={(e) => e.stopPropagation()}
          ref={contextContainerRef}
          style={{
            position: 'fixed',
            top: `${menuPositionRef.current.top}px`,
            left: `${menuPositionRef.current.left}px`,
            minWidth: '150px',
            backgroundColor: 'white',
            border: '1px solid #616161',
            fontSize: '11px',
            zIndex: 1000,
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
          }}
        >
          {listView.renderRightClickContext(rightClickContext, () => setRightClickContext(undefined))}
        </div>
      </Portal>
    );
  }, [rightClickContext]);

  // ref가 연결된 후 메뉴 위치를 조정하는 effect
  useEffect(() => {
    if (rightClickContext && contextContainerRef.current) {
      const { top, left } = calculateMenuPosition(
        rightClickContext.node,
        contextContainerRef.current.offsetWidth,
        contextContainerRef.current.offsetHeight
      );
      contextContainerRef.current.style.top = `${top}px`;
      contextContainerRef.current.style.left = `${left}px`;
    }
  }, [rightClickContext]);

  return {
    onCellRightClick,
    RightClickContextMenu,
  };
};